import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, Calendar, DollarSign } from 'lucide-react';
import { toast } from 'react-toastify';
import Modal from '../Modal';
import FormInput from '../FormInput';
import Pagination from '../Pagination';
import api from '../api';

const ExercicesTab = () => {
  const [exercices, setExercices] = useState([]);
  const [anneesAcademiques, setAnneesAcademiques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedExercice, setSelectedExercice] = useState(null);

  const [formData, setFormData] = useState({
    annee_academique: '',
    date_debut: '',
    date_fin: '',
    est_cloture: false
  });

  useEffect(() => {
    fetchExercices();
    fetchAnneesAcademiques();
  }, []);

  const fetchExercices = async () => {
    try {
      setLoading(true);
      const response = await api.get('/finance/exercices/');
      setExercices(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des exercices:', error);
      toast.error('Erreur lors du chargement des exercices');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnneesAcademiques = async () => {
    try {
      const response = await api.get('/etablissement/annees-academiques/');
      setAnneesAcademiques(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des années académiques:', error);
      toast.error('Erreur lors du chargement des années académiques');
    }
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (modalMode === 'add') {
        await api.post('/finance/exercices/', formData);
        toast.success('Exercice ajouté avec succès');
      } else {
        await api.put(`/finance/exercices/${selectedExercice.id}/`, formData);
        toast.success('Exercice modifié avec succès');
      }

      fetchExercices();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde de l\'exercice');
    }
  };

  const handleEdit = (exercice) => {
    setSelectedExercice(exercice);
    setFormData({
      annee_academique: exercice.annee_academique_id,
      date_debut: exercice.date_debut,
      date_fin: exercice.date_fin,
      est_cloture: exercice.est_cloture
    });
    setModalMode('edit');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet exercice ?')) {
      try {
        await api.delete(`/finance/exercices/${id}/`);
        fetchExercices();
        toast.success('Exercice supprimé avec succès');
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        toast.error('Erreur lors de la suppression de l\'exercice');
      }
    }
  };

  const handleToggleCloture = async (exercice) => {
    try {
      await api.put(`/finance/exercices/${exercice.id}/`, {
        ...exercice,
        est_cloture: !exercice.est_cloture
      });
      fetchExercices();
      toast.success(`Exercice ${exercice.est_cloture ? 'rouvert' : 'clôturé'} avec succès`);
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
      toast.error('Erreur lors du changement de statut de l\'exercice');
    }
  };

  const resetForm = () => {
    setFormData({
      annee_academique: '',
      date_debut: '',
      date_fin: '',
      est_cloture: false
    });
    setSelectedExercice(null);
  };

  const openAddModal = () => {
    resetForm();
    setModalMode('add');
    setShowModal(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // Filtrage des données
  const filteredExercices = exercices.filter(exercice => {
    return exercice.annee_academique_libelle?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredExercices.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredExercices.length / itemsPerPage);

  const anneeOptions = anneesAcademiques.map(annee => ({
    value: annee.id,
    label: annee.libelle
  }));

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202654]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header avec bouton d'ajout et recherche */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="relative">
          <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un exercice..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-transparent"
          />
        </div>
        <button
          onClick={openAddModal}
          className="flex items-center px-4 py-2 bg-[#202654] text-white rounded-lg hover:bg-[#1a1f45] transition-colors"
        >
          <Plus className="h-5 w-5 mr-2" />
          Nouvel Exercice
        </button>
      </div>

      {/* Tableau des exercices */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Année Académique
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Période
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Recettes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Dépenses
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Solde
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    Aucun exercice trouvé
                  </td>
                </tr>
              ) : (
                currentItems.map((exercice) => (
                  <tr key={exercice.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {exercice.annee_academique_libelle}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(exercice.date_debut)} - {formatDate(exercice.date_fin)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      {formatCurrency(exercice.total_recettes)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                      {formatCurrency(exercice.total_depenses)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <span className={exercice.solde >= 0 ? 'text-green-600' : 'text-red-600'}>
                        {formatCurrency(exercice.solde)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                        ${exercice.est_cloture 
                          ? 'bg-red-100 text-red-800' 
                          : 'bg-green-100 text-green-800'
                        }`}>
                        {exercice.est_cloture ? 'Clôturé' : 'En cours'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(exercice)}
                          className="text-blue-600 hover:text-blue-900"
                          disabled={exercice.est_cloture}
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleToggleCloture(exercice)}
                          className={`px-2 py-1 text-xs rounded ${
                            exercice.est_cloture
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-red-100 text-red-800 hover:bg-red-200'
                          }`}
                        >
                          {exercice.est_cloture ? 'Rouvrir' : 'Clôturer'}
                        </button>
                        <button
                          onClick={() => handleDelete(exercice.id)}
                          className="text-red-600 hover:text-red-900"
                          disabled={exercice.est_cloture}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Modal d'ajout/édition */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={modalMode === 'add' ? 'Nouvel Exercice Comptable' : 'Modifier l\'Exercice'}
        size="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormInput
            label="Année Académique"
            type="react-select"
            options={anneeOptions}
            value={formData.annee_academique}
            onChange={(value) => handleInputChange('annee_academique', value)}
            required
          />

          <FormInput
            label="Date de début"
            type="date"
            value={formData.date_debut}
            onChange={(e) => handleInputChange('date_debut', e.target.value)}
            required
          />

          <FormInput
            label="Date de fin"
            type="date"
            value={formData.date_fin}
            onChange={(e) => handleInputChange('date_fin', e.target.value)}
            required
          />

          <div className="flex items-center">
            <input
              type="checkbox"
              id="est_cloture"
              checked={formData.est_cloture}
              onChange={(e) => handleInputChange('est_cloture', e.target.checked)}
              className="h-4 w-4 text-[#202654] focus:ring-[#202654] border-gray-300 rounded"
            />
            <label htmlFor="est_cloture" className="ml-2 text-sm text-gray-700">
              Exercice clôturé
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#202654] text-white rounded-lg hover:bg-[#1a1f45] transition-colors"
            >
              {modalMode === 'add' ? 'Ajouter' : 'Modifier'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ExercicesTab; 